import React, { useState, useMemo } from 'react';
import Layout from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { useFAQs } from '@/hooks';
import { motion } from 'framer-motion';
import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Search, HelpCircle } from 'lucide-react';
import { SEO, createFAQSchema, createBreadcrumbSchema } from '@/components/seo';

const CATEGORIES = [
  { key: '', en: 'All', ar: 'الكل' },
  { key: 'General', en: 'General', ar: 'عام' },
  { key: 'Teachers', en: 'Teachers', ar: 'المعلمون' },
  { key: 'Students', en: 'Students', ar: 'الطلاب' },
  { key: 'Payments', en: 'Payments', ar: 'المدفوعات' },
  { key: 'Technical', en: 'Technical', ar: 'تقني' },
  { key: 'Hifz Program', en: 'Hifz Program', ar: 'برنامج الحفظ' },
  { key: 'Courses', en: 'Courses', ar: 'الدورات' },
];

const FAQ = () => {
  const { isRTL } = useLanguage();
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const { data: faqs, isLoading } = useFAQs(category || undefined);

  const items = (faqs as any[]) || [];

  const filteredFaqs = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((faq: any) =>
      faq.question?.toLowerCase().includes(q) ||
      faq.question_ar?.includes(search) ||
      faq.answer?.toLowerCase().includes(q) ||
      faq.answer_ar?.includes(search)
    );
  }, [items, search]);

  const faqStructuredData = useMemo(() => {
    const localized = items.slice(0, 30).map((faq: any) => ({
      question: isRTL ? (faq.question_ar || faq.question || '') : (faq.question || ''),
      answer: (isRTL ? (faq.answer_ar || faq.answer || '') : (faq.answer || ''))
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim(),
    })).filter(f => f.question && f.answer);

    return {
      "@context": "https://schema.org",
      "@graph": [
        createFAQSchema(localized, isRTL ? 'ar' : 'en'),
        createBreadcrumbSchema([
          { name: isRTL ? 'الرئيسية' : 'Home', url: 'https://hudanlilmuttaqeen.com/' },
          { name: isRTL ? 'الأسئلة الشائعة' : 'FAQ', url: 'https://hudanlilmuttaqeen.com/faq' }
        ])
      ]
    };
  }, [items, isRTL]);

  return (
    <Layout>
      <SEO
        title={isRTL ? 'الأسئلة الشائعة | هُدًى للمتقين' : 'FAQ | Hudan Lil Muttaqeen'}
        description={isRTL
          ? 'إجابات على أكثر الأسئلة شيوعاً حول تعلم القرآن والتجويد والحفظ مع منصة هُدًى للمتقين والمعلمين المعتمدين.'
          : 'Answers to common questions about learning Quran, Tajweed, and Hifz with Hudan Lil Muttaqeen and our certified teachers.'}
        structuredData={faqStructuredData}
      />
      <PageHeader
        title="Frequently Asked Questions"
        titleAr="الأسئلة الشائعة"
        subtitle="Find answers to common questions about our platform"
        subtitleAr="اعثر على إجابات للأسئلة الشائعة حول منصتنا"
        size="sm"
      >
        {/* Search in header */}
        <div className="relative max-w-md">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-100/60" aria-hidden="true" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isRTL ? 'ابحث في الأسئلة...' : 'Search questions...'}
            className="ps-9 bg-white/10 border-white/20 text-white placeholder:text-emerald-100/50 focus:bg-white/15"
            aria-label={isRTL ? 'البحث في الأسئلة الشائعة' : 'Search FAQs'}
            type="search"
          />
        </div>
      </PageHeader>

      <div className="container py-8 space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Category Tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              aria-pressed={category === c.key}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                category === c.key
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {isRTL ? c.ar : c.en}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        {isLoading ? (
          <div className="space-y-3 max-w-3xl">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-14 bg-muted/50 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredFaqs.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-3xl"
          >
            <Accordion type="multiple" className="space-y-2">
              {filteredFaqs.map((faq: any, i: number) => (
                <motion.div
                  key={faq.name || i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <AccordionItem
                    value={faq.name || `faq-${i}`}
                    className="border border-border rounded-xl px-5 data-[state=open]:bg-muted/20"
                  >
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline py-4">
                      <div className="flex items-center gap-3 text-start">
                        {faq.category && (
                          <Badge variant="outline" className="text-[10px] shrink-0">
                            {faq.category}
                          </Badge>
                        )}
                        <span>{isRTL ? (faq.question_ar || faq.question) : faq.question}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground pb-4 pt-0 leading-relaxed">
                      {isRTL ? (faq.answer_ar || faq.answer) : faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>
        ) : (
          <EmptyState
            icon={HelpCircle}
            title={isRTL ? 'لا توجد نتائج' : 'No questions found'}
            description={isRTL ? 'جرب البحث بكلمات مختلفة' : 'Try searching with different keywords'}
          />
        )}
      </div>
    </Layout>
  );
};

export default FAQ;
